import React, { useMemo } from 'react';
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from 'recharts';
import { answerRateTone, formatDuration, formatNumber, formatPercent } from '../../../utils/format';
import { weekActivity } from '../../../utils/userActivity';
import { DrawerFloor } from './DrawerFloor';
import type { AgentRow, VoiceSource } from '../../../types/voip';
import type { FloorRow } from '../../../utils/floor';
const COLORS = {
  outbound: '#00364d',
  inbound: '#7fa3b9',
  missed: '#dc2626'
};
const legend = [{
  label: 'Outbound',
  color: COLORS.outbound
}, {
  label: 'Inbound answered',
  color: COLORS.inbound
}, {
  label: 'Missed',
  color: COLORS.missed
}];
interface DrawerOverviewProps {
  row: AgentRow;
  source: VoiceSource;
  scopeLabel: string;
  floorRow?: FloorRow;
  floorDateLabel: string;
}
export function DrawerOverview({
  row,
  source,
  scopeLabel,
  floorRow,
  floorDateLabel
}: DrawerOverviewProps) {
  const {
    agent,
    stats
  } = row;
  const week = useMemo(() => weekActivity(agent, source), [agent, source]);
  const metrics = [{
    label: 'Made',
    value: formatNumber(stats.outbound),
    tone: 'text-ink'
  }, {
    label: 'Answered',
    value: formatNumber(stats.inbound),
    tone: 'text-ink'
  }, {
    label: 'Missed',
    value: formatNumber(stats.missed),
    tone: 'text-danger'
  }, {
    label: 'Answer rate',
    value: formatPercent(stats.answerRate),
    tone: answerRateTone(stats.answerRate)
  }, {
    label: 'Avg call length',
    value: formatDuration(stats.avgSeconds),
    tone: 'text-ink'
  }];
  return <>
      <section className="p-6" aria-label="Call summary" data-id="element-558" data-mp="21dhouivkke5o">
        <p className="text-xs font-medium text-muted" data-id="element-559" data-mp="2g0t1ko3hm47d">Talk time · {scopeLabel}</p>
        <p className="mt-1 font-display text-[40px] font-bold leading-none tracking-tight text-ink" data-id="element-560" data-mp="2g2w2w4a3bjre">{formatDuration(stats.talkSeconds)}</p>

        <dl className="mt-6 grid grid-cols-3 gap-px overflow-hidden rounded-xl border border-line bg-line" data-id="element-561" data-mp="1nk0630apj5gp">
          {metrics.map(m => <div key={m.label} className="bg-surface p-3" data-id="element-562" data-mp="1ijkvgght8c2f">
              <dt className="text-[11px] text-muted" data-id="element-563" data-mp="3imvige5bnb5q">{m.label}</dt>
              <dd className={`mt-0.5 text-base font-bold ${m.tone}`} data-id="element-564" data-mp="3nmrmgde0r2la">{m.value}</dd>
            </div>)}
          <div className="bg-surface p-3" data-id="element-565" data-mp="3sd80nzpnys8p">
            <dt className="text-[11px] text-muted" data-id="element-566" data-mp="3se9kotonqp2k">Total calls</dt>
            <dd className="mt-0.5 text-base font-bold text-ink" data-id="element-567" data-mp="a747imjk3bg">{formatNumber(stats.totalCalls)}</dd>
          </div>
        </dl>
      </section>

      {floorRow && <DrawerFloor row={floorRow} dateLabel={floorDateLabel} data-id="element-568" data-mp="2zrf78gq9cpyl" />}

      <section className="border-t border-line p-6" aria-labelledby="drawer-activity-heading" data-id="element-569" data-mp="34op3ogc2e1vx">
        <div className="flex items-baseline justify-between gap-3" data-id="element-570" data-mp="38ozafv7okwbu">
          <h3 id="drawer-activity-heading" className="text-sm font-semibold text-ink" data-id="element-571" data-mp="3dpd7yjbnonvm">
            Call activity
          </h3>
          <p className="text-xs text-muted" data-id="element-572" data-mp="nyqchy5lcg3j">Last 7 days</p>
        </div>
        <ul className="mt-2 flex flex-wrap gap-3" data-id="element-573" data-mp="sy36d2uvqnfm">
          {legend.map(l => <li key={l.label} className="flex items-center gap-1.5 text-[11px] text-muted" data-id="element-574" data-mp="1329vu7jejvd7">
              <span className="h-2 w-2 rounded-sm" style={{
            backgroundColor: l.color
          }} aria-hidden data-id="element-575" data-mp="y3glgnzewkh9" />
              {l.label}
            </li>)}
        </ul>
        <div className="mt-3 h-36" data-id="element-576" data-mp="tax73oswhlbe">
          <ResponsiveContainer width="100%" height="100%" data-id="element-577" data-mp="pbeioj1ves3t">
            <BarChart data={week} margin={{
            top: 4,
            right: 0,
            left: 0,
            bottom: 0
          }} barCategoryGap="28%" data-id="element-578" data-mp="pawg74om7hng">
              <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{
              fontSize: 11,
              fill: '#8c99a7'
            }} data-id="element-579" data-mp="k9yx8pvs6jkw" />
              <Tooltip cursor={{
              fill: 'rgba(0,54,77,0.04)'
            }} contentStyle={{
              borderRadius: 10,
              border: '1px solid #e4e9ef',
              fontSize: 12
            }} data-id="element-580" data-mp="ffbgjd70qtgv" />
              <Bar dataKey="outbound" name="Outbound" stackId="d" fill={COLORS.outbound} animationDuration={300} data-id="element-581" data-mp="6edakpd0sbvn" />
              <Bar dataKey="inbound" name="Inbound answered" stackId="d" fill={COLORS.inbound} animationDuration={300} data-id="element-582" data-mp="20qbm4xysxjup" />
              <Bar dataKey="missed" name="Missed" stackId="d" fill={COLORS.missed} radius={[3, 3, 0, 0]} animationDuration={300} data-id="element-583" data-mp="27t03mcscsxot" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
    </>;
}